import {
  ChevronDown,
  ListMusic,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useMusicPlayer } from "@/music/MusicPlayer";
import { formatDuration } from "@/music/library";

// ── The player bar (Spotify-pattern, AgenticOS voice) ─────────────────────
//
// A persistent bar at the BOTTOM of the app shell. It never leaves: the audio
// element lives above the routing, so the music survives every navigation.
//
// Truth rules (the bar never invents a state):
//   • No track loaded → the bar shows a quiet "pick something to play" line.
//   • The file cannot be reached (the audio is on Kit's Mac; the deployed
//     backend mounts it in a later phase) → "local only" plus the headphone
//     note for beds. The bar does NOT show a spinner forever and does not
//     pretend to be playing.
//   • The elapsed time comes from the element, never from a guess.

const HeadphoneNote = ({ track }) => {
  if (!track || !track.headphones_required) return null;
  return (
    <span
      data-testid="player-bar-headphones"
      className="shrink-0 rounded-chip border border-os-bronze border-b-os-bronze-strong bg-os-gold-tint px-2 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-2"
    >
      Headphones
    </span>
  );
};

const VolumeIcon = ({ volume }) => {
  if (volume <= 0.01) return <VolumeX className="h-4 w-4" strokeWidth={1.75} />;
  if (volume < 0.5)
    return <Volume1 className="h-4 w-4" strokeWidth={1.75} />;
  return <Volume2 className="h-4 w-4" strokeWidth={1.75} />;
};

const IconButton = ({ label, onClick, disabled, children, testid, active }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "inline-flex h-8 w-8 items-center justify-center rounded-control text-os-ink-2 transition-colors",
      disabled
        ? "cursor-not-allowed text-os-ink-4"
        : "hover:bg-os-wash hover:text-os-ink",
      active && "bg-os-wash text-os-ink",
    )}
  >
    {children}
  </button>
);

// ── The timeline scrubber ───────────────────────────────────────────────────
//
// The seek line was display-only; Jeff asked to move through the song. This
// is a real range input: click anywhere to jump, drag to sweep, arrow keys
// for precision, touch-friendly. While dragging, the thumb follows the FINGER
// (the local draft), not playback — releasing the drag commits one seek. The
// fill keeps tracking playback when nobody is touching it.
const SeekSlider = ({ total, currentTime, onSeek, disabled }) => {
  const [draft, setDraft] = useState(null); // seconds, while dragging

  // Total unknown (metadata not loaded yet): fall back to the catalog's
  // duration_s (the bar already did this math); with neither, the slider is
  // inert but still rendered — the bar never hides the line.
  const max = Math.max(1, Math.round(Number(total) || 0));
  const shown = draft !== null ? draft : currentTime;

  return (
    <div
      data-testid="player-bar-seek"
      role="slider"
      aria-label="Playback position"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={`${formatDuration(shown)} of ${formatDuration(max)}`}
      aria-disabled={disabled || undefined}
      className={cn(
        "group relative h-4 w-full",
        disabled ? "cursor-default" : "cursor-pointer",
      )}
    >
      {/* the fill line: the visual at half height, behind the thumb */}
      <div
        className="pointer-events-none absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 bg-os-wash"
        aria-hidden="true"
      >
        <div
          data-testid="player-bar-seek-fill"
          className="h-full bg-os-ink"
          style={{ width: `${Math.min(100, (shown / max) * 100)}%` }}
        />
      </div>
      <input
        data-testid="player-bar-seek-input"
        type="range"
        min={0}
        max={max}
        step={1}
        value={Math.round(shown)}
        disabled={disabled}
        aria-hidden="false"
        onChange={(e) => {
          const v = Number(e.target.value) || 0;
          setDraft(v);
          onSeek(v); // live-scrub: audio follows while dragging
        }}
        onPointerUp={() => setDraft(null)}
        onBlur={() => setDraft(null)}
        className={cn(
          "absolute inset-0 h-full w-full appearance-none bg-transparent",
          "focus-visible:outline-none",
          // the native track/thumb is invisible; the painted line above IS the track
          "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-os-ink [&::-webkit-slider-thumb]:opacity-0 group-hover:[&::-webkit-slider-thumb]:opacity-100",
          "[&::-moz-range-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-os-ink [&::-moz-range-thumb]:opacity-0 group-hover:[&::-moz-range-thumb]:opacity-100",
        )}
      />
    </div>
  );
};

export const MusicPlayerBar = ({ capture = null }) => {
  const player = useMusicPlayer();
  const {
    currentTrack,
    playing,
    currentTime,
    duration,
    volume,
    queue,
    index,
    isLocalOnly,
    status,
    queueOpen,
  } = player;

  const hasTrack = Boolean(currentTrack);
  const total = duration || Number(currentTrack?.duration_s) || 0;

  return (
    <div
      data-testid="music-player-bar"
      role="region"
      aria-label="Music player"
      className="sticky bottom-0 z-40 border-t border-os-border bg-white font-inter"
    >
      {/* ── the seek line: interactive ── */}
      <SeekSlider total={total} currentTime={currentTime} onSeek={player.seek} disabled={!hasTrack} />

      <div className="flex h-16 items-center gap-3 px-4 md:px-6">
        {/* ── now playing ── */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="min-w-0">
            <div
              data-testid="player-bar-title"
              className={cn(
                "truncate text-[0.875rem] font-semibold",
                hasTrack ? "text-os-ink" : "text-os-ink-4",
              )}
            >
              {hasTrack ? currentTrack.title : "Nothing playing"}
            </div>
            <div className="flex items-center gap-2">
              <span
                data-testid="player-bar-subtitle"
                className="truncate font-mono text-[0.6875rem] text-os-ink-3"
              >
                {hasTrack
                  ? `${currentTrack.artist || "Kit"}${
                      currentTrack.theme_label
                        ? ` · ${currentTrack.theme_label}`
                        : ""
                    }`
                  : "Pick something from the library"}
              </span>
              <HeadphoneNote track={currentTrack} />
            </div>
          </div>
        </div>

        {/* ── transport ── */}
        <div className="flex shrink-0 items-center gap-1">
          <IconButton
            label="Previous track"
            testid="player-bar-prev"
            onClick={player.prev}
            disabled={!hasTrack || index <= 0}
          >
            <SkipBack className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <button
            type="button"
            aria-label={playing ? "Pause" : "Play"}
            data-testid="player-bar-toggle"
            onClick={player.toggle}
            disabled={!hasTrack}
            className={cn(
              "inline-flex h-9 w-9 items-center justify-center rounded-full border border-os-border",
              hasTrack
                ? "text-os-ink hover:bg-os-wash"
                : "cursor-not-allowed text-os-ink-4",
            )}
          >
            {playing ? (
              <Pause className="h-4 w-4" strokeWidth={1.75} />
            ) : (
              <Play className="h-4 w-4" strokeWidth={1.75} />
            )}
          </button>
          <IconButton
            label="Next track"
            testid="player-bar-next"
            onClick={player.next}
            disabled={!hasTrack || index < 0 || index >= queue.length - 1}
          >
            <SkipForward className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </div>

        {/* ── the clock ── */}
        <div
          data-testid="player-bar-time"
          className="hidden shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-3 sm:block"
        >
          {formatDuration(currentTime)} / {formatDuration(total)}
        </div>

        {/* ── volume ── */}
        <div className="hidden items-center gap-2 md:flex">
          <label
            className="sr-only"
            htmlFor="player-bar-volume"
            data-testid="player-bar-volume-label"
          >
            Volume
          </label>
          <VolumeIcon volume={volume} />
          <input
            id="player-bar-volume"
            data-testid="player-bar-volume"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={(e) => player.setVolume(Number(e.target.value))}
            className="h-1 w-20 cursor-pointer appearance-none rounded-full bg-os-wash accent-os-ink"
          />
        </div>

        {/* ── the queue ── */}
        <IconButton
          label={queueOpen ? "Hide queue" : "Show queue"}
          testid="player-bar-queue"
          onClick={player.toggleQueue}
          disabled={!hasTrack}
          active={queueOpen}
        >
          <ListMusic className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>

        {/* ── idea capture (desk only) lives IN the bar, far right — Jeff 10/29:
             never floats over the play controls, the bar's content stays left ── */}
        {capture ? (
          <>
            <div
              aria-hidden="true"
              className="mx-1 h-6 w-px shrink-0 bg-os-border md:mx-2"
            />
            {capture}
          </>
        ) : null}
      </div>

      {/* ── the local-only notice: the file is on Kit's Mac, not here ── */}
      {hasTrack && isLocalOnly && (
        <div
          data-testid="player-bar-local-only"
          className="flex items-center gap-2 border-t border-os-border bg-os-wash px-4 py-2 md:px-6"
        >
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-amber">
            local only
          </span>
          <span className="text-[0.8125rem] text-os-ink-2">
            The audio for this track lives on Kit&rsquo;s machine. The catalog
            knows it; this browser cannot reach it yet.
          </span>
        </div>
      )}

      {/* ── the queue list ── */}
      {queueOpen && hasTrack && (
        <div
          data-testid="player-bar-queue-list"
          className="max-h-64 overflow-y-auto border-t border-os-border px-4 py-2 md:px-6"
        >
          {queue.map((t, i) => (
            <button
              type="button"
              key={t.id || `${t.date}/${t.slot_id}`}
              data-testid={`player-bar-queue-item-${t.slot_id}`}
              onClick={() => player.playTrack(t, queue)}
              className={cn(
                "flex w-full items-center gap-3 rounded-control px-2 py-1.5 text-left",
                i === index ? "bg-os-wash" : "hover:bg-os-wash",
              )}
            >
              <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-os-ink">
                {t.title}
              </span>
              <span className="shrink-0 font-mono text-[0.6875rem] text-os-ink-4">
                {formatDuration(t.duration_s)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* The status is exposed for the tests and for the screen reader; it is
          the bar's own truth about the element, not a guess. */}
      <span data-testid="player-bar-status" className="sr-only">
        {status}
      </span>
    </div>
  );
};

export default MusicPlayerBar;
